import LayersIcon from "@mui/icons-material/Layers";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import HubIcon from "@mui/icons-material/Hub";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import { Box, Skeleton, Typography } from "@mui/material";
import { COLORS } from "../theme/theme-options";
import { ReactNode } from "react";
import type { ServiceTemplate } from "../types";

const serviceTemplateIcons: Record<ServiceTemplate["type"], ReactNode> = {
  saas: <LayersIcon />,
  retail: <ViewInArIcon />,
  banking: <AssuredWorkloadIcon />,
  internal_it: <HubIcon />,
};

export const ServiceTemplatePicker: React.FC<{
  onSelect: (serviceTemplate: string) => void;
  selected: string;
  templates: ServiceTemplate[];
  loading?: boolean;
}> = ({ onSelect, selected, templates, loading = false }) => {
  const cardStyles = {
    background: COLORS.grey[800],
    borderRadius: "10px",
    border: `1px solid ${COLORS.grey[600]}`,
    minHeight: 156,
  };

  if (loading) {
    return (
      <Box display="flex" flexWrap="wrap" gap={2}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Box
            key={`template-skeleton-${index}`}
            flex="1 1 calc(50% - 8px)"
            padding={2}
            gap={1}
            display="flex"
            flexDirection="column"
            sx={cardStyles}
          >
            <Box display="flex" gap={1}>
              <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2 }} />
              <Box display="flex" flexDirection="column" flex={1}>
                <Skeleton variant="text" width="70%" height={26} />
                <Skeleton variant="text" width="45%" height={20} />
              </Box>
            </Box>
            <Box>
              <Skeleton variant="text" width="100%" height={22} />
              <Skeleton variant="text" width="78%" height={22} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={2}>
      {templates.map((t) => (
        <Box
          key={t.id}
          onClick={() => onSelect(t.id)}
          flex="1 1 calc(50% - 8px)"
          padding={2}
          gap={1}
          display="flex"
          flexDirection="column"
          sx={{
            ...cardStyles,
            cursor: "pointer",
            ...(selected === t.id
              ? {
                  boxShadow:
                    "0 0 20px rgba(6, 182, 212, 0.15), 0 0 60px rgba(6, 182, 212, 0.05)",
                  borderColor: "rgb(6 182 212 / 0.3)",
                  background: "rgb(6 182 212 / 0.1)",
                }
              : {}),
          }}
        >
          <Box display="flex" gap={1}>
            <Box
              sx={{
                background: COLORS.grey[700],
                color: COLORS.grey[400],
                ...(selected === t.id
                  ? {
                      background: "rgb(6 182 212 / 0.2)",
                      color: "rgb(34 211 238 / 1)",
                    }
                  : {}),
              }}
              p={1}
              borderRadius={2}
              width={40}
              height={40}
            >
              {serviceTemplateIcons[t.type]}
            </Box>
            <Box display="flex" flexDirection="column">
              <Typography>{t.name}</Typography>
              <Typography color={COLORS.grey[500]}>{t.type}</Typography>
            </Box>
          </Box>
          <Box>
            <Typography color={COLORS.grey[300]}>{t.description}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
